import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { get, uri } from "helpers/api_helper";
import axios from "axios";
import ModalView from "./ModalView";

// Responsive style helper
const getResponsiveStyles = (width) => {
  const isMobile = width <= 576;
  const isTablet = width > 576 && width <= 768;

  return {
    dropzoneStyles: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: isMobile ? "10px" : isTablet ? "15px" : "20px",
      border: "2px dashed #1B6FED",
      borderRadius: "8px",
      backgroundColor: "#F5FCFF",
      color: "#777",
      cursor: "pointer",
      marginBottom: "10px",
      minHeight: isMobile ? "100px" : isTablet ? "120px" : "150px",
      width: "100%",
      boxSizing: "border-box",
      position: "relative", // Anchor for absolute positioning
    },
    uploadIconStyle: {
      fontSize: isMobile ? "1.5em" : isTablet ? "1.8em" : "2em",
      margin: "0",
    },
    browseStyle: {
      color: "#2196f3",
      textDecoration: "underline",
      cursor: "pointer",
      fontSize: isMobile ? "0.9em" : "1em",
    },
    errorStyle: {
      color: "red",
      fontSize: isMobile ? "0.8em" : "0.9em",
      textAlign: "center",
      marginTop: "5px",
    },
    fileNameStyle: {
      fontSize: isMobile ? "0.8em" : isTablet ? "0.85em" : "0.9em",
      color: "#333",
      marginTop: isMobile ? "4px" : "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      maxWidth: isMobile ? "70%" : "80%", // Prevent overflow
      whiteSpace: isMobile ? "normal" : "nowrap",
      overflow: "hidden",
      textOverflow: isMobile ? "clip" : "ellipsis",
      textAlign: "center",
    },
    removeButtonStyle: {
      position: "absolute",
      top: isMobile ? "5px" : isTablet ? "8px" : "10px", // Inside top-right corner
      right: isMobile ? "5px" : isTablet ? "8px" : "10px",
      background: "transparent",
      border: "none",
      fontSize: isMobile ? "1em" : isTablet ? "1.1rem" : "1.25rem",
      cursor: "pointer",
      color: "red",
      padding: isMobile ? "3px" : "5px", // Touch-friendly
      zIndex: 10, // Above other elements
    },
    innerDivStyle: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      textAlign: "center",
      fontSize: isMobile ? "0.9em" : "1em",
    },
    iconButtonStyle: {
      background: "transparent",
      border: "none",
      padding: isMobile ? "5px" : "0",
      cursor: "pointer",
      outline: "none",
      ":hover": { background: "transparent" },
      ":focus": { background: "transparent", outline: "none" },
      fontSize: isMobile ? "1.2em" : isTablet ? "1.5em" : "1.8em",
    },
  };
};

const DragAndDropComponent = ({
  initialFile = {},
  handleFileUpload,
  readOnly = false,
}) => {
  const [errorMessage, setErrorMessage] = useState("");
  const uploadedFiles = initialFile;
  const [isUploading, setIsUploading] = useState(false);
  const [modal, setModal] = useState({ open: false, url: "" });
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const styles = getResponsiveStyles(windowWidth);

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (readOnly) return;
      if (acceptedFiles?.length > 0) {
        setIsUploading(true);
        const file = acceptedFiles[0];
        const formData = new FormData();
        formData.append("file", file);

        try {
          const { data: response } = await axios.post(
            `${uri}/fileservice/api/files/upload`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );

          const fileWithId = {
            id: response.id,
            fileName: file.name, // Use original file name
            name: file.name, // Use original file name
            path: response.filePath || `/Uploads/${file.name}`,
          };

          handleFileUpload(fileWithId);
          setErrorMessage("");
        } catch (error) {
          console.error("File upload error:", error);
          setErrorMessage(
            error.code === "ERR_NETWORK"
              ? "Failed to upload due to a network issue."
              : `Failed to upload file "${file.name}". Please try again.`
          );
        } finally {
          setIsUploading(false);
        }
      }
    },
    [handleFileUpload, readOnly]
  );

  const onDropRejected = useCallback(
    (fileRejections) => {
      if (readOnly) return;
      handleFileUpload?.({});
      fileRejections.forEach(({ file, errors }) => {
        errors.forEach((error) => {
          if (error.code === "file-too-large") {
            setErrorMessage(
              `Error: "${file.name}" is too large. Max size is 2MB.`
            );
          } else if (error.code === "file-invalid-type") {
            setErrorMessage(`Error: "${file.name}" has an invalid file type.`);
          } else {
            setErrorMessage(`Error with "${file.name}": ${error.message}`);
          }
        });
      });
    },
    [handleFileUpload, readOnly]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    onDropRejected,
    accept: {
      // "application/pdf": [".pdf"],
      // "application/msword": [".doc"],
      // "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      //   [".docx"],
      // "image/jpeg": [".jpg", ".jpeg"],
      // "image/png": [".png"],
      // "image/gif": [".gif"],
      "application/pdf": [],
      "application/msword": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
    },
    maxFiles: 1,
    // maxSize: 2 * 1024 * 1024,
    noClick: readOnly,
    noKeyboard: true,
    disabled: readOnly,
  });

  const removeFile = (e) => {
    if (readOnly) return;
    handleFileUpload({});
    setErrorMessage("");
    e.stopPropagation();
  };

  const viewFile = async (id) => {
    try {
      const response = await get(`${uri}/fileservice/api/files/view/${id}`);
      if (response.fileContent) {
        const byteCharacters = atob(response.fileContent);
        const byteArray = new Uint8Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteArray[i] = byteCharacters.charCodeAt(i);
        }

        const fileTypeList = response?.fileName?.split(".");
        const fileType = fileTypeList[1]?.toLowerCase();
        const blob = new Blob([byteArray], { type: `application/${fileType}` });
        const url = URL.createObjectURL(blob);

        if (fileType !== "pdf") {
          setModal((prev) => ({ ...prev, open: true, url }));
        } else {
          window.open(url, "_blank");
        }
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const handleViewFile = (e) => {
    e.preventDefault();
    if (uploadedFiles.id) {
      viewFile(uploadedFiles.id);
    }
    e.stopPropagation();
  };

  const handleModal = () => {
    setModal((prev) => ({ ...prev, open: false, url: "" }));
  };

  // console.log("Current file:", uploadedFiles, "readOnly:", readOnly);

  return (
    <>
      <div {...getRootProps()} style={styles.dropzoneStyles}>
        <input {...getInputProps()} />
        {isUploading ? (
          <p>Uploading...</p>
        ) : readOnly && !uploadedFiles.id ? (
          <p>No file selected</p>
        ) : isDragActive && !readOnly ? (
          <p>Drop the file here...</p>
        ) : (
          <>
            {uploadedFiles.id ? (
              <button
                type="button"
                onClick={handleViewFile}
                style={styles.iconButtonStyle}
              >
                <h2 style={{ margin: 0, fontSize: "inherit" }}>📄</h2>
              </button>
            ) : (
              !readOnly && (
                <div style={styles.innerDivStyle}>
                  <p style={styles.uploadIconStyle}>⬆️</p>
                  <p>
                    Drag & Drop files or{" "}
                    <span style={styles.browseStyle} onClick={open}>
                      Browse files
                    </span>
                  </p>
                </div>
              )
            )}
            {uploadedFiles.id && (
              <>
                <h5 style={styles.fileNameStyle}>
                  {uploadedFiles.fileName || uploadedFiles.name}
                </h5>
                {!readOnly && (
                  <button
                    onClick={removeFile}
                    style={styles.removeButtonStyle}
                    aria-label="Remove file"
                  >
                    ×
                  </button>
                )}
              </>
            )}
          </>
        )}
      </div>
      {modal.open && (
        <ModalView handleModal={handleModal} modal={modal}>
          <div id="modal-content"></div>
        </ModalView>
      )}
      {errorMessage && !readOnly && (
        <p style={styles.errorStyle}>{errorMessage}</p>
      )}
    </>
  );
};

export default DragAndDropComponent;
